import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowSelectedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";

import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { RollStatusSelector } from "@/shared/selectors";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { RollOperation } from "../../../models/Operation";
import { useOperationOptionsFunctions } from "../hooks/useOperationOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { useOperations } from "../OperationPage";
import { OperationDetails } from "./OperationDetails";

export const TableOperation = ({ partId }: { partId: string | undefined }) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [statusFilter, setStatusFilter] = useState<string | null>(null);
	const [filterValue, setFilterValue] = useState("");
	const [selectedRowData, setSelectedRowData] = useState<RollOperation | null>(
		null,
	);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");
	const [error, setError] = useState("");

	const { data: rows, isLoading } = useOperations((error) =>
		setError(error.message),
	);

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useOperationOptionsFunctions({
		setSelectedRowData,
		setShowModal,
	});
	const { columnDefs } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});

	const { defaultColDef } = useGridDefinitions({});

	const onRowSelected = (event: RowSelectedEvent) => {
		const cell = gridApi?.getFocusedCell();
		if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
			setSelectedRowData(event.data);
			setShowModal("update");
			navigate(`/rollshop-operations/${(event.data as RollOperation).PartId}`);
		}
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "operationsContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	const onModalHide = async ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => {
		if (close) {
			setShowModal("");
			setSelectedRowData(null);
			if (gridApi) gridApi?.deselectAll();
			return;
		}
		if (entityId && shouldUpdate) {
			await queryClient.invalidateQueries(["operations"]);
			setTimeout(() => {
				const rowNode = gridApi?.getRowNode(String(entityId));

				if (rowNode!) {
					gridApi?.ensureNodeVisible(rowNode);
					rowNode.setSelected(true);
					setSelectedRowData(rowNode.data);
				}
			}, 0);
		}
	};

	const handleNewRoll = () => {
		if (gridApi) gridApi.deselectAll();
		setShowModal("create");
		setSelectedRowData(null);
		navigate(`/rollshop-operations/new`);
	};

	useEffect(() => {
		if (!isLoading) {
			if (partId) {
				if (partId === "new") {
					handleNewRoll();
				} else {
					const partNode = gridApi?.getRowNode(partId);

					if (partNode) {
						gridApi?.ensureNodeVisible(partNode);

						partNode.setSelected(true);
						setSelectedRowData(partNode.data);
						setShowModal("update");
					}
				}
			} else {
				setSelectedRowData(null);
				setShowModal("");
			}
		}
	}, [isLoading, gridApi, rows]);

	useEffect(() => {
		registerConfig({
			id: "operationsContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid2 container justifyContent="center">
				<Grid2 size={{ md: 12, xs: 12 }} style={{ padding: "0 15px" }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Rollshop Operations
					</Typography>
				</Grid2>
				<Grid2
					container
					spacing={1}
					style={{ margin: "10px 18px" }}
					alignItems="center"
					justifyContent="space-between"
				>
					<Grid2 size={{ xs: 12, md: 2 }}>
						<RollStatusSelector
							label="Status"
							value={statusFilter}
							onChange={(status: string) => setStatusFilter(status)}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 10 }}>
						<TextFieldSearchFilter
							value={filterValue}
							onChange={(val) => {
								setFilterValue(val);
								gridApi?.setGridOption("quickFilterText", val);
							}}
						/>
					</Grid2>
				</Grid2>
				<Grid2
					container
					justifyContent="flex-start"
					alignItems="center"
					style={{ width: "100%", padding: "0 10px" }}
					spacing={2}
				>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<Paper elevation={2} style={{ height: "72vh", width: "100%" }}>
							<AgGridReact<RollOperation>
								loading={isLoading}
								rowData={
									rows?.filter(
										(r) => !statusFilter || r.RollStatusCode === statusFilter,
									) || []
								}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={34}
								animateRows
								getContextMenuItems={getContextMenuItems}
								onGridReady={(params) => setGridApi(params.api)}
								getRowId={(params) => String(params.data.PartId)}
								loadingOverlayComponent={CenteredLazyLoading}
								onRowSelected={onRowSelected}
								gridOptions={{
									rowSelection: {
										mode: "singleRow",
										checkboxes: false,
										enableClickSelection: true,
									},
								}}
							/>
						</Paper>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<Paper
							elevation={2}
							style={{ height: "72vh", width: "100%", padding: 15 }}
						>
							<OperationDetails
								roll={selectedRowData}
								onHide={onModalHide}
								modal={showModal}
							/>
						</Paper>
					</Grid2>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
